// Source: CMS Medicare Claims Processing Manual, Chapter 10
// https://www.cms.gov/regulations-and-guidance/guidance/manuals/downloads/clm104c10.pdf
// Cross-referenced with CGS Medicare Home Health Billing Codes:
// https://www.cgsmedicare.com/hhh/claims/hh_billing_codes.html
// and Palmetto GBA Home Health Billing Codes Job Aid:
// https://dominoapps.palmettogba.com/palmetto/providers.nsf/files/HH_Billing_Codes_Job_Aid.pdf
//
// The homeHealth flag indicates codes relevant to HHA claims per CGS/Palmetto HH billing guidance.
// Notable HHA-specific rules:
//   - Code 50 (OASIS assessment date) is REQUIRED on all period-of-care claims (TOB 32X, from 1/1/2020)
//   - Code 55 (Date of Death) is REQUIRED when patient discharge status = 20 (expired)
//   - Codes 61/62 are optional but affect PDGM institutional admission payment grouping

export interface OccurrenceCode {
  code: string;
  description: string;
  /** Used on HHA claims per CGS Medicare and Palmetto GBA HH billing guidance */
  homeHealth: boolean;
}

export const OCCURRENCE_CODES: OccurrenceCode[] = [
  {
    code: '01',
    description:
      'Accident/Medical Coverage — Injury with Medical Payment Coverage',
    homeHealth: false,
  },
  {
    code: '02',
    description: 'No-Fault Insurance Involved — Including Auto Accident/Other',
    homeHealth: false,
  },
  { code: '03', description: 'Accident/Tort Liability', homeHealth: false },
  { code: '04', description: 'Accident/Employment Related', homeHealth: false },
  {
    code: '05',
    description: 'Accident/No Medical or Liability Coverage',
    homeHealth: false,
  },
  { code: '06', description: 'Crime Victim', homeHealth: false },
  {
    code: '09',
    description: 'Start of Infertility Treatment Cycle',
    homeHealth: false,
  },
  { code: '10', description: 'Last Menstrual Period', homeHealth: false },
  { code: '11', description: 'Onset of Symptoms/Illness', homeHealth: true },
  {
    code: '12',
    description: 'Date of Onset for a Chronically Dependent Individual',
    homeHealth: false,
  },
  { code: '16', description: 'Date of Last Therapy', homeHealth: false },
  {
    code: '17',
    description:
      'Date Outpatient Occupational Therapy Plan Established or Last Reviewed',
    homeHealth: true,
  },
  {
    code: '18',
    description: 'Date of Retirement — Patient/Beneficiary',
    homeHealth: false,
  },
  { code: '19', description: 'Date of Retirement — Spouse', homeHealth: false },
  {
    code: '20',
    description: 'Date Guarantee of Payment Began',
    homeHealth: false,
  },
  { code: '21', description: 'Date UR Notice Received', homeHealth: false },
  { code: '22', description: 'Date Active Care Ended', homeHealth: false },
  {
    code: '23',
    description: 'Payer Code — Hospice Election Period Cancellation',
    homeHealth: false,
  },
  { code: '24', description: 'Date Insurance Denied', homeHealth: false },
  {
    code: '25',
    description: 'Date Benefits Terminated by Primary Payer',
    homeHealth: false,
  },
  {
    code: '26',
    description: 'Date SNF Bed Became Available',
    homeHealth: false,
  },
  {
    code: '27',
    description: 'Date of Hospice Certification or Recertification',
    homeHealth: false,
  },
  {
    code: '28',
    description:
      'Date Comprehensive Outpatient Rehabilitation Plan Established or Last Reviewed',
    homeHealth: false,
  },
  {
    code: '29',
    description:
      'Date Outpatient Physical Therapy Plan Established or Last Reviewed',
    homeHealth: true,
  },
  {
    code: '30',
    description:
      'Date Outpatient Speech Pathology Plan Established or Last Reviewed',
    homeHealth: true,
  },
  {
    code: '31',
    description: 'Date Beneficiary Notified of Intent to Bill (Accommodations)',
    homeHealth: false,
  },
  {
    code: '32',
    description:
      'Date Beneficiary Notified of Intent to Bill — ABN Issuance Date',
    homeHealth: true,
  },
  {
    code: '33',
    description:
      'First Day of Coordination Period for ESRD Beneficiaries Covered by EGHP',
    homeHealth: false,
  },
  {
    code: '34',
    description: 'Date of Election of Extended Care Facilities',
    homeHealth: false,
  },
  {
    code: '35',
    description: 'Date Treatment Started for Physical Therapy',
    homeHealth: true,
  },
  {
    code: '36',
    description:
      'Date of Inpatient Hospital Discharge for Covered Transplant Patient',
    homeHealth: false,
  },
  {
    code: '37',
    description:
      'Date of Inpatient Hospital Discharge for Non-Covered Transplant Patient',
    homeHealth: false,
  },
  {
    code: '38',
    description: 'Date Treatment Started for Home IV Therapy',
    homeHealth: true,
  },
  {
    code: '39',
    description: 'Date Discharged on a Continuous Course of IV Therapy',
    homeHealth: true,
  },
  { code: '40', description: 'Scheduled Date of Admission', homeHealth: false },
  {
    code: '41',
    description: 'Date of First Test for Pre-Admission Testing',
    homeHealth: false,
  },
  {
    code: '42',
    description:
      'Date of Discharge — Hospice Discharge Due to Patient Revocation',
    homeHealth: false,
  },
  {
    code: '43',
    description: 'Scheduled Date of Canceled Surgery',
    homeHealth: false,
  },
  {
    code: '44',
    description: 'Date Treatment Started for Occupational Therapy',
    homeHealth: true,
  },
  {
    code: '45',
    description: 'Date Treatment Started for Speech Therapy',
    homeHealth: true,
  },
  {
    code: '46',
    description: 'Date Treatment Started for Cardiac Rehabilitation',
    homeHealth: false,
  },
  {
    code: '47',
    description: 'Date Cost Outlier Status Begins',
    homeHealth: false,
  },
  {
    code: '50',
    description:
      'OASIS Assessment Completion Date (M0090) — Required on all TOB 32X claims from 1/1/2020',
    homeHealth: true,
  },
  { code: '51', description: 'Date of Last Kt/V Reading', homeHealth: false },
  {
    code: '52',
    description: 'Medical Certification/Recertification Date',
    homeHealth: false,
  },
  { code: '54', description: 'Physician Follow-Up Date', homeHealth: false },
  {
    code: '55',
    description:
      'Date of Death — Required when discharge status = 20 (Expired)',
    homeHealth: true,
  },
  {
    code: '56',
    description: 'Original Hospice Election or Revocation Date',
    homeHealth: false,
  },
  {
    code: '61',
    description:
      'Acute Care Hospital Discharge Date (HHA Only) — Affects PDGM institutional grouping',
    homeHealth: true,
  },
  {
    code: '62',
    description:
      'Other Institutional Discharge Date — SNF/IRF/LTCH/IPF (HHA Only) — Affects PDGM grouping',
    homeHealth: true,
  },
  { code: 'A1', description: 'Birthdate — Insured A', homeHealth: false },
  {
    code: 'A2',
    description: 'Effective Date — Insured A Policy',
    homeHealth: false,
  },
  {
    code: 'A3',
    description: 'Benefits Exhausted — Payer A',
    homeHealth: false,
  },
  { code: 'A4', description: 'Split Bill Date', homeHealth: false },
  { code: 'B1', description: 'Birthdate — Insured B', homeHealth: false },
  {
    code: 'B2',
    description: 'Effective Date — Insured B Policy',
    homeHealth: false,
  },
  {
    code: 'B3',
    description: 'Benefits Exhausted — Payer B',
    homeHealth: false,
  },
  { code: 'C1', description: 'Birthdate — Insured C', homeHealth: false },
  {
    code: 'C2',
    description: 'Effective Date — Insured C Policy',
    homeHealth: false,
  },
  {
    code: 'C3',
    description: 'Benefits Exhausted — Payer C',
    homeHealth: false,
  },
];
